import React, { useState, useEffect } from 'react';
import { Calendar, Truck, CreditCard, CheckCircle, Clock } from 'lucide-react';
import { supabase } from '../lib/supabaseClient';
import SiteContentText from './SiteContentText';
import {
  correctHomepageStep3Description,
  correctHomepageStep3Title,
} from '../utils/homepageBookingCopy';

const HowItWorks: React.FC = () => {
  const [content, setContent] = useState({
    title: '',
    subtitle: '',
    step1_title: '',
    step1_description: '',
    step2_title: '',
    step2_description: '',
    step3_title: '',
    step3_description: '',
    step4_title: '',
    step4_description: ''
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchContent();
  }, []);

  const fetchContent = async () => {
    try {
      const { data, error } = await supabase
        .from('site_content')
        .select('key, content')
        .eq('category', 'how_it_works');

      if (error) throw error;

      const contentMap = data.reduce((acc, item) => {
        acc[item.key.replace('how_it_works_', '')] = item.content;
        return acc;
      }, {} as any);

      setContent({
        title: contentMap.title || 'Så här fungerar det',
        subtitle: contentMap.subtitle || 'Enkel process från bokning till leverans – vi gör det enkelt att hyra festutrustning.',
        step1_title: contentMap.step1_title || 'Välj produkter',
        step1_description: contentMap.step1_description || 'Bläddra bland vårt sortiment och välj de produkter som passar ditt event.',
        step2_title: contentMap.step2_title || 'Boka datum',
        step2_description: contentMap.step2_description || 'Välj datum för din bokning och se direkt tillgänglighet i kalendern.',
        step3_title: correctHomepageStep3Title(contentMap.step3_title),
        step3_description: correctHomepageStep3Description(contentMap.step3_description),
        step4_title: contentMap.step4_title || 'Leverans & upphämtning',
        step4_description: contentMap.step4_description || 'Vi levererar och hämtar utrustningen direkt till och från din festlokal.'
      });
    } catch (err) {
      console.error('Error fetching how it works content:', err);
      // Keep default values if fetch fails
    } finally {
      setLoading(false);
    }
  };

  const steps = [
    {
      icon: <Calendar className="w-12 h-12 text-purple-600" />,
      title: content.step1_title,
      description: content.step1_description
    },
    {
      icon: <CreditCard className="w-12 h-12 text-purple-600" />,
      title: content.step2_title,
      description: content.step2_description
    },
    {
      icon: <Truck className="w-12 h-12 text-purple-600" />,
      title: content.step3_title,
      description: content.step3_description
    },
    {
      icon: <CheckCircle className="w-12 h-12 text-purple-600" />,
      title: content.step4_title,
      description: content.step4_description
    }
  ];

  if (loading) {
    return (
      <section className="py-16 md:py-20 border-t border-slate-800">
        <div className="container mx-auto px-4 max-w-6xl">
          <div className="animate-pulse text-center mb-10">
            <div className="h-8 bg-slate-700 rounded w-48 mx-auto mb-2"></div>
            <div className="h-4 bg-slate-700 rounded w-72 mx-auto"></div>
          </div>
          <div className="relative">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="relative flex flex-col items-start md:items-center md:text-center">
                  {i < 4 && (
                    <div className="hidden md:block absolute right-[-24px] top-10 w-12 h-px bg-slate-700/60" aria-hidden />
                  )}
                  <div className="w-12 h-12 bg-slate-700 rounded-full mb-4 mx-auto" />
                  <div className="h-4 bg-slate-700 rounded w-3/4 mb-3 mx-auto" />
                  <div className="h-3 bg-slate-700 rounded w-full" />
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="py-16 md:py-20 border-t border-slate-800">
      <div className="container mx-auto px-4 max-w-6xl">
        <div className="text-center mb-10">
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-2">
            {content.title}
          </h2>
          <SiteContentText
            as="p"
            className="text-slate-400 max-w-2xl mx-auto"
            text={content.subtitle}
          />
        </div>

        <div className="relative">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            {steps.map((step, index) => (
              <div
                key={index}
                className="relative flex flex-col items-start md:items-center md:text-center"
              >
                {index < steps.length - 1 && (
                  <div
                    className="hidden md:block absolute right-[-24px] top-10 w-12 h-px bg-slate-800/60"
                    aria-hidden
                  />
                )}

                <div className="relative flex items-center gap-4 mb-4 z-10 md:flex-col md:items-center md:text-center">
                  <div className="relative w-12 h-12 rounded-full border border-slate-700 bg-slate-900/35 flex items-center justify-center">
                    <div className="text-purple-300">
                      {React.cloneElement(step.icon, { className: 'w-6 h-6 text-purple-300' })}
                    </div>
                    <div className="absolute -top-2 -right-2 w-6 h-6 rounded-full bg-slate-950 border border-slate-800 flex items-center justify-center text-[11px] font-bold text-purple-200">
                      {index + 1}
                    </div>
                  </div>

                  <h3 className="text-lg font-bold text-white">{step.title}</h3>
                </div>

                <SiteContentText
                  as="p"
                  className="text-slate-400 text-sm leading-relaxed z-10 md:max-w-[220px]"
                  text={step.description}
                />
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default HowItWorks;